import React from 'react';
import { Box, Card, IconButton, Stack, Typography } from '@mui/material';
import { AddRounded, Inventory2Outlined, ScaleRounded } from '@mui/icons-material';

const money = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  maximumFractionDigits: 0,
});

export default function ProductCard({ producto, onAgregar }) {
  const stockDisponible = producto?.stock != null ? Number(producto.stock) : null;
  const sinStock = stockDisponible != null && stockDisponible <= 0;
  const esPesable = producto?.pesable === true;

  const handleAdd = (event) => {
    event?.stopPropagation?.();
    onAgregar?.(producto);
  };

  return (
    <Card
      variant="outlined"
      onClick={handleAdd}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 158,
        '@media (max-height: 820px)': { minHeight: 142 },
        borderRadius: 1.5,
        borderColor: '#dbe5ef',
        bgcolor: '#fff',
        boxShadow: '0 6px 16px rgba(25, 59, 96, .055)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color .16s ease, box-shadow .16s ease, transform .16s ease',
        '&:hover': {
          borderColor: '#9fc2ee',
          boxShadow: '0 10px 24px rgba(23, 82, 145, .10)',
          transform: 'translateY(-1px)',
        },
        '&:active': { transform: 'translateY(0)' },
      }}
    >
      <Box
        sx={{
          p: 1.2,
          '@media (max-height: 820px)': { p: 0.95 },
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <Stack direction="row" spacing={1.15} sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 78,
              height: 84,
              '@media (max-height: 820px)': { width: 66, height: 72 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              borderRadius: 1.8,
              bgcolor: '#f4f7fb',
              border: '1px solid #edf1f6',
              p: 0.65,
            }}
          >
            {producto.imagen ? (
              <Box
                component="img"
                src={producto.imagen}
                alt={producto.nombre}
                sx={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
              />
            ) : esPesable ? (
              <ScaleRounded sx={{ fontSize: 40, color: '#9fb0c3' }} />
            ) : (
              <Inventory2Outlined sx={{ fontSize: 40, color: '#9fb0c3' }} />
            )}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1, pt: 0.1 }}>
            <Typography
              variant="body2"
              fontWeight={950}
              sx={{
                color: '#17263a',
                fontSize: 14.25,
                '@media (max-height: 820px)': { fontSize: 13.25 },
                lineHeight: 1.15,
                minHeight: 33,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {producto.nombre}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block', mt: 0.4, fontSize: 11.15 }}
            >
              SKU: {producto.sku || producto.codigoBarras || 'Sin código'}
            </Typography>

            {stockDisponible != null ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.45,
                  color: sinStock ? '#dc2626' : '#159447',
                  fontWeight: 900,
                  fontSize: 11.4,
                }}
              >
                {sinStock ? 'Sin stock' : `Stock: ${stockDisponible}`}
              </Typography>
            ) : esPesable ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.45, color: '#159447', fontWeight: 900 }}>
                Producto pesable
              </Typography>
            ) : null}
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 0.45 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#0874ef',
                fontSize: 24,
                '@media (max-height: 820px)': { fontSize: 21 },
                fontWeight: 1000,
                letterSpacing: '-.04em',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {money.format(producto.precioBruto || 0)}
            </Typography>
            {esPesable ? (
              <Typography variant="caption" color="text.secondary" fontWeight={800}>
                por kg
              </Typography>
            ) : null}
          </Box>

          <IconButton
            onClick={handleAdd}
            aria-label={`Agregar ${producto.nombre}`}
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1.5,
              bgcolor: '#e8f2ff',
              color: '#0874ef',
              border: '1px solid #d4e8ff',
              '&:hover': { bgcolor: '#d9eaff' },
            }}
          >
            <AddRounded />
          </IconButton>
        </Stack>
      </Box>
    </Card>
  );
}
